/**
 * Size guide modal (ring products).
 *
 * - Inserts the ACF option "need_help" (rendered in data-resize on .product-container.ring)
 *   right after the "Size" product add-on, wrapped in .size-guide-help.
 * - Clicking a link inside that block opens the target (PDF / image / page) in an
 *   accessible modal instead of a new tab. Modifier / middle clicks keep the default behaviour.
 *
 * Labels are translatable through data attributes on .product-container (single-product.twig):
 * data-size-guide-title, data-size-guide-close, data-size-guide-open, data-size-guide-download.
 * If an image is set in the ACF option "size_guide_image" (data-size-guide-image), the modal shows
 * that image on every screen, with a button to download the linked document.
 */
const HOST_CLASS = "size-guide-help";
const MODAL_ID = "size-guide-modal";
const TRANSITION_MS = 350;
let modal = null;
let lastTrigger = null;
let closeTimer;
let labels = {
    title: "Size guide",
    close: "Close",
    open: "Open in a new tab",
    download: "Download the document",
};
let guideImage = "";
const prefersReducedMotion = () => window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
// iOS / Android render PDFs in iframes poorly (first page only, or a download prompt).
const isTouchDevice = () => {
    const ua = navigator.userAgent || "";
    const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const coarse = window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
    return iOS || /Android/i.test(ua) || coarse;
};
const kindOf = (url) => {
    const path = url.split("#")[0].split("?")[0].toLowerCase();
    if (/\.pdf$/.test(path))
        return "pdf";
    if (/\.(jpe?g|png|webp|gif|avif|svg)$/.test(path))
        return "image";
    return "page";
};
const escapeHtml = (value) => value.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const focusables = (root) => Array.from(root.querySelectorAll('a[href], button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])')).filter((el) => el.offsetParent !== null || el.tagName === "IFRAME");
/* ---------------------------------------------------------------- Modal DOM */
const buildModal = () => {
    const el = document.createElement("div");
    el.id = MODAL_ID;
    el.className = "size-guide";
    el.setAttribute("hidden", "");
    el.innerHTML = `
        <div class="size-guide__overlay" data-size-guide-dismiss></div>
        <div class="size-guide__panel" role="dialog" aria-modal="true" aria-labelledby="${MODAL_ID}-title" tabindex="-1">
            <header class="size-guide__header">
                <h2 class="size-guide__title" id="${MODAL_ID}-title"></h2>
                <button type="button" class="size-guide__close" data-size-guide-dismiss aria-label="${escapeHtml(labels.close)}">
                    <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" focusable="false">
                        <line x1="3" y1="3" x2="17" y2="17" stroke="currentColor" stroke-width="1"/>
                        <line x1="17" y1="3" x2="3" y2="17" stroke="currentColor" stroke-width="1"/>
                    </svg>
                </button>
            </header>
            <div class="size-guide__body"></div>
            <footer class="size-guide__footer"></footer>
        </div>`;
    document.body.appendChild(el);
    el.addEventListener("click", (e) => {
        const target = e.target;
        if (target.closest("[data-size-guide-dismiss]")) {
            e.preventDefault();
            closeModal();
        }
    });
    el.addEventListener("keydown", onKeydown);
    return el;
};
const renderContent = (url, title) => {
    const body = modal.querySelector(".size-guide__body");
    const footer = modal.querySelector(".size-guide__footer");
    const kind = kindOf(url);
    const safeUrl = escapeHtml(url);
    const safeTitle = escapeHtml(title);
    const openLink = `<a class="size-guide__link" href="${safeUrl}" target="_blank" rel="noopener">${escapeHtml(labels.open)}</a>`;
    body.className = `size-guide__body size-guide__body--${kind}`;
    footer.innerHTML = "";
    footer.hidden = false;
    if (guideImage) {
        // Image du guide (option ACF) : lisible partout, y compris sur mobile ; le document reste téléchargeable
        body.className = "size-guide__body size-guide__body--image";
        body.innerHTML = `<img class="size-guide__image" src="${escapeHtml(guideImage)}" alt="${safeTitle}" decoding="async">`;
        footer.innerHTML = `<a class="size-guide__button" href="${safeUrl}" target="_blank" rel="noopener" download>${escapeHtml(labels.download)}</a>`;
    }
    else if (kind === "image") {
        body.innerHTML = `<img class="size-guide__image" src="${safeUrl}" alt="${safeTitle}" decoding="async">`;
        footer.hidden = true;
    }
    else if (kind === "pdf" && isTouchDevice()) {
        // Mobile browsers: no reliable inline PDF viewer -> prominent fallback.
        body.classList.add("size-guide__body--fallback");
        body.innerHTML = `
            <div class="size-guide__fallback">
                <p class="size-guide__fallback-name">${safeTitle}</p>
                <a class="size-guide__button" href="${safeUrl}" target="_blank" rel="noopener">${escapeHtml(labels.open)}</a>
            </div>`;
        footer.hidden = true;
    }
    else {
        const src = kind === "pdf" ? `${url.split("#")[0]}#view=FitH` : url;
        body.innerHTML = `<iframe class="size-guide__frame" src="${escapeHtml(src)}" title="${safeTitle}" loading="lazy"></iframe>`;
        footer.innerHTML = openLink;
    }
};
/* ---------------------------------------------------------- Open / close */
const lockScroll = () => {
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.classList.add("size-guide-open");
    if (scrollbar > 0)
        document.body.style.paddingRight = `${scrollbar}px`;
};
const unlockScroll = () => {
    document.documentElement.classList.remove("size-guide-open");
    document.body.style.paddingRight = "";
};
const openModal = (url, trigger) => {
    if (!modal)
        modal = buildModal();
    window.clearTimeout(closeTimer);
    lastTrigger = trigger;
    const title = labels.title;
    modal.querySelector(".size-guide__title").textContent = title;
    renderContent(url, title);
    modal.removeAttribute("hidden");
    lockScroll();
    // Force reflow so the transition runs from the hidden state.
    void modal.offsetWidth;
    modal.classList.add("is-open");
    const close = modal.querySelector(".size-guide__close");
    (close || modal.querySelector(".size-guide__panel")).focus({ preventScroll: true });
};
const closeModal = () => {
    if (!modal || !modal.classList.contains("is-open"))
        return;
    modal.classList.remove("is-open");
    unlockScroll();
    const finish = () => {
        modal.setAttribute("hidden", "");
        // Unload iframe (stops PDF viewer / media).
        modal.querySelector(".size-guide__body").innerHTML = "";
    };
    if (prefersReducedMotion())
        finish();
    else
        closeTimer = window.setTimeout(finish, TRANSITION_MS);
    if (lastTrigger && document.contains(lastTrigger)) {
        lastTrigger.focus({ preventScroll: true });
    }
    lastTrigger = null;
};
function onKeydown(e) {
    if (e.key === "Escape" || e.key === "Esc") {
        e.preventDefault();
        closeModal();
        return;
    }
    if (e.key !== "Tab")
        return;
    const items = focusables(modal.querySelector(".size-guide__panel"));
    if (!items.length) {
        e.preventDefault();
        return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !modal.contains(active))) {
        e.preventDefault();
        last.focus();
    }
    else if (!e.shiftKey && (active === last || !modal.contains(active))) {
        e.preventDefault();
        first.focus();
    }
}
/* ------------------------------------------------------------ Triggers */
const onTriggerClick = (e) => {
    const link = e.target.closest(`.${HOST_CLASS} a[href]`);
    if (!link)
        return;
    // Keep native behaviour: middle click, ctrl/cmd/shift/alt click.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
        return;
    const raw = (link.getAttribute("href") || "").trim();
    if (!raw || raw.charAt(0) === "#" || /^(mailto|tel|javascript):/i.test(raw))
        return;
    e.preventDefault();
    openModal(link.href, link);
};
/**
 * Inserts the "need help" block after the Size add-on (ring products) and
 * binds the size guide modal. Safe to call several times.
 */
export function initSizeGuide() {
    const container = document.querySelector(".product-container.ring");
    if (!container)
        return;
    const d = container.dataset;
    labels = {
        title: d.sizeGuideTitle || labels.title,
        close: d.sizeGuideClose || labels.close,
        open: d.sizeGuideOpen || labels.open,
        download: d.sizeGuideDownload || labels.download,
    };
    guideImage = (d.sizeGuideImage || "").trim();
    if (!document.querySelector(`.${HOST_CLASS}`)) {
        const html = container.getAttribute("data-resize");
        // Product Add-ons put data-addon-name on both <label> and <h2>: insert only once.
        const sizeAddon = document.querySelector("*[data-addon-name='Size']");
        if (html && html.trim() && sizeAddon) {
            const host = document.createElement("div");
            host.className = HOST_CLASS;
            host.innerHTML = html;
            sizeAddon.insertAdjacentElement("afterend", host);
            // Link text is the natural "opens a dialog" hint for assistive tech.
            host.querySelectorAll("a[href]").forEach((a) => {
                const href = (a.getAttribute("href") || "").trim();
                if (href && href.charAt(0) !== "#")
                    a.setAttribute("aria-haspopup", "dialog");
            });
        }
    }
    if (!document.body.__sizeGuideBound) {
        document.body.__sizeGuideBound = true;
        document.addEventListener("click", onTriggerClick);
    }
}
export default initSizeGuide;
